import { format } from "date-fns";
import { CalendarIcon, CircleX } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DateFilterFieldProps = {
  date: Date | undefined;
  id: string;
  label: string;
  onChange: (date: Date | undefined) => void;
  placeholder: string;
  fromDate?: Date;
  className?: string;
  hideLabel?: boolean;
};

export function DateFilterField({
  date,
  id,
  label,
  onChange,
  placeholder,
  fromDate,
  className,
  hideLabel = false,
}: DateFilterFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex min-w-[160px] flex-col gap-1", className)}>
      <label
        htmlFor={id}
        className={cn(
          "text-xs font-medium text-muted-foreground",
          hideLabel && "sr-only"
        )}
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              className={cn(
                "h-10 rounded-md justify-start text-left font-normal flex-1",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 size-4" aria-hidden="true" />
              {date ? format(date, "MMM d, yyyy") : placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={4}
            className="z-50 rounded-md border bg-popover p-0 shadow-md focus:outline-none"
          >
            <Calendar
              mode="single"
              selected={date}
              onSelect={(selectedDate) => {
                onChange(selectedDate ?? undefined);
                setOpen(false);
              }}
              disabled={fromDate ? { before: fromDate } : undefined}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground self-stretch"
          disabled={!date}
          onClick={() => onChange(undefined)}
          aria-label={`Clear ${label.toLowerCase()}`}
        >
          <CircleX className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export default DateFilterField;
